import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  fr: {
    // Navigation
    dashboard: 'Tableau de bord',
    courses: 'Cours',
    students: 'Étudiants',
    trainers: 'Formateurs',
    schedule: 'Emploi du temps',
    classrooms: 'Salles',
    payments: 'Paiements',
    benefits: 'Bénéfices',
    reports: 'Rapports',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    
    // Common
    add: 'Ajouter',
    edit: 'Modifier',
    delete: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    search: 'Rechercher',
    filter: 'Filtrer',
    export: 'Exporter',
    import: 'Importer',
    loading: 'Chargement...',
    
    // Auth
    login: 'Se connecter',
    email: 'Email',
    password: 'Mot de passe',
    rememberMe: 'Se souvenir de moi',
    forgotPassword: 'Mot de passe oublié ?',
    
    // Students
    studentName: 'Nom de l\'étudiant',
    studentCode: 'Code étudiant',
    phone: 'Téléphone',
    address: 'Adresse',
    birthDate: 'Date de naissance',
    joinDate: 'Date d\'inscription',
    status: 'Statut',
    active: 'Actif',
    inactive: 'Inactif',
    
    // Courses
    courseType: 'Type de cours',
    startDate: 'Date de début',
    endDate: 'Date de fin',
    price: 'Prix',
    duration: 'Durée',
    courseTitle: 'Titre du cours',
    courseDescription: 'Description du cours',
    
    // Classrooms
    classroomName: 'Nom de la salle',
    classroomType: 'Type de salle',
    capacity: 'Capacité',
    
    // Payments
    amount: 'Montant',
    paymentMethod: 'Méthode de paiement',
    paymentDate: 'Date de paiement',
    invoice: 'Facture',
    
    // Dashboard
    totalStudents: 'Total des étudiants',
    activeStudents: 'Étudiants actifs',
    monthlyRevenue: 'Revenus mensuels',
    classroomStatus: 'État des salles',
    
    // Trainers
    trainerName: 'Nom du formateur',
    speciality: 'Spécialité',
    experience: 'Expérience',
    
    // Schedule
    scheduleTitle: 'Titre',
    scheduleDate: 'Date',
    scheduleTime: 'Heure',
    trainer: 'Formateur',
    classroom: 'Salle',
  },
  ar: {
    // Navigation
    dashboard: 'لوحة القيادة',
    courses: 'الدورات',
    students: 'الطلاب',
    trainers: 'المدربون',
    schedule: 'الجدول الزمني',
    classrooms: 'القاعات',
    payments: 'المدفوعات',
    benefits: 'الأرباح',
    reports: 'التقارير',
    settings: 'الإعدادات',
    logout: 'تسجيل الخروج',
    
    // Common
    add: 'إضافة',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    search: 'بحث',
    filter: 'تصفية',
    export: 'تصدير',
    import: 'استيراد',
    loading: 'جاري التحميل...',
    
    // Auth
    login: 'تسجيل الدخول',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    rememberMe: 'تذكرني',
    forgotPassword: 'نسيت كلمة المرور؟',
    
    // Students
    studentName: 'اسم الطالب',
    studentCode: 'رمز الطالب',
    phone: 'الهاتف',
    address: 'العنوان',
    birthDate: 'تاريخ الميلاد',
    joinDate: 'تاريخ التسجيل',
    status: 'الحالة',
    active: 'نشط',
    inactive: 'غير نشط',
    
    // Courses
    courseType: 'نوع الدورة',
    startDate: 'تاريخ البداية',
    endDate: 'تاريخ النهاية',
    price: 'السعر',
    duration: 'المدة',
    courseTitle: 'عنوان الدورة',
    courseDescription: 'وصف الدورة',
    
    // Classrooms
    classroomName: 'اسم القاعة',
    classroomType: 'نوع القاعة',
    capacity: 'السعة',
    
    // Payments
    amount: 'المبلغ',
    paymentMethod: 'طريقة الدفع',
    paymentDate: 'تاريخ الدفع',
    invoice: 'الفاتورة',
    
    // Dashboard
    totalStudents: 'إجمالي الطلاب',
    activeStudents: 'الطلاب النشطون',
    monthlyRevenue: 'الإيرادات الشهرية',
    classroomStatus: 'حالة القاعات',
    
    // Trainers
    trainerName: 'اسم المدرب',
    speciality: 'التخصص',
    experience: 'الخبرة',
    
    // Schedule
    scheduleTitle: 'العنوان',
    scheduleDate: 'التاريخ',
    scheduleTime: 'الوقت',
    trainer: 'المدرب',
    classroom: 'القاعة',
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');
  const [isLoading, setIsLoading] = useState(true);

  // Load language preference from localStorage
  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem('language') || 'fr';
      setLanguage(savedLanguage);
    } catch (error) {
      console.error('Error fetching language preference:', error);
      setLanguage('fr');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update language preference to localStorage
  useEffect(() => {
    if (!isLoading) {
      try {
        localStorage.setItem('language', language);
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    }

    // Update document attributes
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language, isLoading]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
    }
  };

  const value = {
    language,
    changeLanguage,
    t,
    isRTL: language === 'ar',
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}; 